import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  CalendarBlank,
  LockKey,
  Crown,
  ArrowUpRight,
  Ticket,
  Buildings,
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

const fmtDate = (start, end) => {
  try {
    const s = new Date(start);
    const opts = { month: "short", day: "numeric" };
    const sStr = s.toLocaleDateString("en-US", opts);
    if (end) {
      const e = new Date(end);
      const eStr = e.toLocaleDateString("en-US", { ...opts, year: "numeric" });
      return `${sStr} – ${eStr}`;
    }
    return s.toLocaleDateString("en-US", { ...opts, year: "numeric" });
  } catch {
    return start;
  }
};

export const ShowCard = ({ show, index, onOpen, onTickets, onUpgrade, archived = false }) => {
  const locked = show.locked;

  return (
    <motion.article
      data-testid={`show-card-${show.id}`}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.4) }}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white/[0.03] transition-transform duration-200 hover:-translate-y-1 ${
        show.is_premium
          ? "border-amber-500/25 hover:border-amber-500/50"
          : "border-white/10 hover:border-white/25"
      }`}
    >
      <div className="relative h-40 overflow-hidden">
        <img
          src={show.image_url}
          alt={show.name}
          className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12141F] via-[#12141F]/40 to-transparent" />
        <div className="absolute left-3 top-3 flex gap-2">
          <Badge className="border-0 bg-black/50 text-[11px] font-medium text-white backdrop-blur-md">
            {show.category}
          </Badge>
        </div>
        {show.is_premium && (
          <div className="absolute right-3 top-3">
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/15 px-2.5 py-1 text-[11px] font-semibold text-amber-300 backdrop-blur-md">
              <Crown size={12} weight="fill" /> Premium
            </span>
          </div>
        )}
        {archived && (
          <div className="absolute right-3 bottom-3">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-700/80 px-2.5 py-1 text-[11px] font-semibold text-slate-200 backdrop-blur-md">
              Event Ended
            </span>
          </div>
        )}
        {show.distance_km != null && (
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/80 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-md">
              <MapPin size={12} weight="fill" /> {show.distance_km} km away
            </span>
          </div>
        )}
      </div>

      <div className="relative flex flex-1 flex-col p-6">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <MapPin size={14} weight="fill" className="text-blue-400" />
          {show.city}, {show.country}
        </div>
        <h3 className="mt-2 font-display text-lg font-bold leading-snug tracking-tight text-white">
          {show.name}
        </h3>
        <div className="mt-2 flex items-center gap-2 text-sm text-slate-400">
          <CalendarBlank size={15} className="text-slate-500" />
          {fmtDate(show.start_date, show.end_date)}
        </div>

        {/* Premium locked content */}
        {locked ? (
          <div className="relative mt-4 flex-1">
            <div className="blur-locked space-y-2 text-sm text-slate-300">
              <p>{show.venue || "Exclusive venue details"}</p>
              <p>Exhibitors: 000+ · Attendees: 00,000+</p>
              <p>Top brands: ••••••••, ••••••, ••••••</p>
              <p>Investment range: •••••••••</p>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-xl bg-black/45 p-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-400/15 ring-1 ring-amber-400/40">
                <LockKey size={22} weight="fill" className="text-amber-300" />
              </div>
              <p className="text-center text-xs text-slate-300">
                Exclusive exhibitor &amp; deal data locked
              </p>
              <button
                data-testid={`upgrade-card-btn-${show.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onUpgrade();
                }}
                className="rounded-full bg-amber-400 px-4 py-2 text-xs font-bold text-black transition-colors duration-200 hover:bg-amber-300"
              >
                Unlock for $9.99
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400 line-clamp-3">
            {show.description}
          </p>
        )}

        <div className="mt-6 flex items-center gap-2">
          <button
            data-testid={`view-details-btn-${show.id}`}
            onClick={() => onOpen(show)}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-white/25 hover:bg-white/10"
          >
            View Details <ArrowUpRight size={15} weight="bold" />
          </button>
          {!archived && (
            <button
              data-testid={`tickets-btn-${show.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onTickets(show);
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-500"
            >
              <Ticket size={15} weight="fill" /> Tickets
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
};
